import React, { useState } from 'react';
import { List, Kanban, MessageSquare, Plus, CheckSquare, Edit3, Trash2, Archive, Lock, MessageCircle, Paperclip } from "lucide-react";
import { Badge, PRIORITIES, timeAgo, UserAvatar } from "../App";

export default function TaskBoard({ ctx, filteredTasks, tasksByStatus, projectView, setProjectView, markChatRead }) {
  const { 
    T, t, canEdit, canView, activeProject, activeProjectId, 
    projectStatuses, getStatusMeta, setDetailTask, openCreateTask, 
    updateTaskStatus, archiveTask, deleteTask, milestones, users, 
    getTaskAssigneeIds, ACCENT_LIGHT, unreadChat, canCreateTask,
    allTasks // 🚀 Wir brauchen allTasks, um Unteraufgaben zu finden!
  } = ctx;

  // --- DRAG & DROP STATE ---
  const [draggedTaskId, setDraggedTaskId] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);

  if (!activeProject) return null;
  if (!canView) return (
    <div style={{display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",height:"60%",gap:16,color:T.textFaint}}>
      <Lock size={48} />
      <div style={{fontSize:16,fontWeight:600}}>{t.noAccess||"Kein Zugriff"}</div>
    </div>
  );

  const tPrio = (lbl) => t?.[lbl?.toLowerCase()] || lbl;

  // --- DRAG & DROP HANDLER ---
  const handleDragStart = (e, taskId) => {
    setDraggedTaskId(taskId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", taskId);
    setTimeout(() => { e.target.style.opacity = "0.5"; }, 0);
  };

  const handleDragEnd = (e) => {
    e.target.style.opacity = "1";
    setDraggedTaskId(null);
    setDragOverColumn(null);
  };

  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverColumn !== status) setDragOverColumn(status);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setDragOverColumn(null);
  };

  const handleDrop = (e, targetStatus) => {
    e.preventDefault();
    setDragOverColumn(null);
    const taskId = e.dataTransfer.getData("text/plain");
    const task = filteredTasks.find(t => t.id === parseInt(taskId));
    if (task && task.status !== targetStatus) {
      updateTaskStatus(task.id, targetStatus);
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      
      {/* LISTEN ANSICHT */}
      {projectView === "list" && (
        <div style={{background:T.card, border:`1px solid ${T.border}`, borderRadius:16, overflow:"hidden"}}>
          <div className="task-list-header">
            <span>{t.task ? t.task.toUpperCase() : "AUFGABE"}</span>
            <span>STATUS</span>
            <span>PRIORITÄT</span>
            <span>MILESTONE</span>
            <span>TAGS</span>
            <span>ZUGEWIESEN</span>
            <span></span>
          </div>

          {filteredTasks.length === 0 && (
            <div style={{padding:"60px", textAlign:"center", color:T.textGhost}}>
              <div style={{fontSize:36, marginBottom:12}}>◎</div>
              <div>{t.noTasks||"Keine Aufgaben in dieser Ansicht"}</div>
            </div>
          )}

          {filteredTasks.map((task, i) => {
            const sm = getStatusMeta(task.status);
            const ms = milestones.find(m => m.id === task.milestone_id);
            const assigneeIds = getTaskAssigneeIds(task);
            const cDone = (task.checklist || []).filter(c => c.done).length;
            const cTotal = (task.checklist || []).length;
            
            // 🚀 NEU: Sub-Tasks für diese Karte zählen
            const mySubTasks = allTasks.filter(t => t.parent_task_id === task.id && !t.archived);
            const mySubTasksDone = mySubTasks.filter(t => t.status === "Erledigt" || t.status === "Fix").length;

            return (
              <div key={task.id} className="tr task-row" onClick={() => setDetailTask(task)} style={{ animation: `fadeUp ${0.1 + i * 0.03}s ease` }}>
                <div style={{minWidth:0}}>
                  <div style={{fontSize:13, fontWeight:500, color:T.textSub, display:"flex", alignItems:"center", gap:6}}>
                    {task.dependencies?.length > 0 && <Lock size={12} title="Blockiert" />}
                    {task.title}
                  </div>
                  <div style={{display:"flex", gap:6, alignItems:"center", marginTop: 4, flexWrap:"wrap"}}>
                    {cTotal > 0 && <span style={{fontSize:10, color:cDone===cTotal?"#34D399":T.textFaint, background:T.cardHover, padding:"1px 6px", borderRadius:5}}>✓ {cDone}/{cTotal}</span>}
                    {(task.comments?.length > 0) && <span style={{fontSize:10, color:T.textFaint, background:T.cardHover, padding:"1px 6px", borderRadius:5}}><MessageCircle size={10} style={{marginRight:3}}/>{task.comments.length}</span>}
                    {(task.files?.length > 0) && <span style={{fontSize:10, color:T.textFaint, background:T.cardHover, padding:"1px 6px", borderRadius:5}}><Paperclip size={10} style={{marginRight:3}}/>{task.files.length}</span>}
                    {/* 🚀 NEU: Anzeige der Unteraufgaben in der Liste */}
                    {mySubTasks.length > 0 && <span style={{fontSize:10, color:mySubTasksDone===mySubTasks.length?"#34D399":T.textMuted, background:T.cardHover, border:`1px solid ${T.border2}`, padding:"1px 6px", borderRadius:5}}>↳ {mySubTasksDone}/{mySubTasks.length} Unteraufgaben</span>}
                  </div>
                </div>

                <div onClick={e => e.stopPropagation()}>
                  {canEdit ? (
                    <select value={task.status} onChange={e => { e.stopPropagation(); updateTaskStatus(task.id, e.target.value); }} style={{background:sm.bg, color:sm.color, border:`1px solid ${sm.color}40`, borderRadius:8, padding:"5px 8px", fontSize:11, fontWeight:600, outline:"none", cursor:"pointer"}}>
                      {projectStatuses.map(s => <option key={s} value={s}>{s}</option>)}
                    </select>
                  ) : <Badge label={task.status} color={sm.color} bg={sm.bg}/>}
                </div>

                <Badge label={tPrio(PRIORITIES[task.priority]?.label)} color={PRIORITIES[task.priority]?.color} bg={PRIORITIES[task.priority]?.bg}/>
                
                <div>{ms && <span style={{fontSize:10, background:ms.color+"15", color:ms.color, padding:"3px 8px", borderRadius:6, fontWeight:600}}>🎯 {ms.name}</span>}</div>
                
                <div style={{display:"flex", gap:4, flexWrap:"wrap"}}>
                  {[...(task.tags || [])]
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .slice(0, 2)
                    .map(tg => <span key={tg.id} style={{fontSize:10, background:tg.color+"15", color:tg.color, padding:"3px 8px", borderRadius:6, fontWeight:600}}>{tg.name}</span>)}
                </div>

                <div style={{display:"flex"}}>
                  {assigneeIds.slice(0, 3).map((uid, idx) => <UserAvatar key={uid} user={users.find(u => u.id === uid)} size={26} overlap idx={idx}/>)}
                </div>

                <div style={{display:"flex", gap:6, justifyContent:"flex-end"}}>
                  {canEdit && (
                    <>
                      <div className="del" onClick={e => { e.stopPropagation(); archiveTask(task.id); }} style={{width:26, height:26, borderRadius:7, background:"rgba(52,211,153,0.1)", display:"flex", alignItems:"center", justifyContent:"center", color:"#34D399"}}><CheckSquare size={14}/></div>
                      <div className="del" onClick={e => { e.stopPropagation(); deleteTask(task.id); }} style={{width:26, height:26, borderRadius:7, background:"rgba(255,71,87,0.1)", display:"flex", alignItems:"center", justifyContent:"center", color:"#FF4757"}}><Trash2 size={14}/></div>
                    </>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* BOARD ANSICHT (KANBAN MIT DRAG & DROP) */}
      {projectView === "board" && (
        <div className="board-grid" style={{display:"grid", gridTemplateColumns:`repeat(${projectStatuses.length}, 1fr)`, gap:16, alignItems:"flex-start"}}>
          {projectStatuses.map(status => {
            const sm = getStatusMeta(status);
            const columnTasks = filteredTasks.filter(t => t.status === status);
            const isDragOver = dragOverColumn === status;

            return (
              <div 
                key={status} 
                onDragOver={(e) => canEdit ? handleDragOver(e, status) : null}
                onDragLeave={canEdit ? handleDragLeave : null}
                onDrop={(e) => canEdit ? handleDrop(e, status) : null}
                style={{
                  background: isDragOver ? T.cardHover : T.card, 
                  border: `1px solid ${isDragOver ? ACCENT_LIGHT : T.border}`, 
                  borderRadius: 16, 
                  overflow: "hidden", 
                  minHeight: 400,
                  transition: "all 0.2s ease"
                }}
              >
                <div style={{padding:"14px 16px", background:"rgba(0,0,0,0.1)", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", gap:8}}>
                  <div style={{width:8, height:8, borderRadius:"50%", background:sm.dot}} />
                  <span style={{fontSize:13, fontWeight:700, color:sm.color, flex:1}}>{status}</span>
                  <span style={{fontSize:11, color:T.textGhost, fontWeight:700}}>{columnTasks.length}</span>
                </div>
                
                <div style={{padding:12, display:"flex", flexDirection:"column", gap:10}}>
                  {columnTasks.map(task => {
                    // 🚀 NEU: Sub-Tasks für die Kanban-Karte zählen
                    const mySubTasks = allTasks.filter(t => t.parent_task_id === task.id && !t.archived);
                    const mySubTasksDone = mySubTasks.filter(t => t.status === "Erledigt" || t.status === "Fix").length;

                    return (
                      <div 
                        key={task.id} 
                        draggable={canEdit}
                        onDragStart={(e) => handleDragStart(e, task.id)}
                        onDragEnd={handleDragEnd}
                        onClick={() => setDetailTask(task)} 
                        style={{
                          background: draggedTaskId === task.id ? T.sidebar : T.cardHover, 
                          padding: 14, 
                          borderRadius: 12, 
                          border: `1px solid ${draggedTaskId === task.id ? ACCENT_LIGHT : T.border2}`, 
                          cursor: canEdit ? "grab" : "pointer", 
                          transition: "all 0.1s"
                        }} 
                        className="hov"
                      >
                        <div style={{fontSize:13, fontWeight:500, color:T.textSub, marginBottom:10}}>{task.title}</div>
                        
                        {/* 🚀 NEU: Anzeige der Unteraufgaben auf der Karte */}
                        {mySubTasks.length > 0 && (
                          <div style={{fontSize:10, color:mySubTasksDone===mySubTasks.length?"#34D399":T.textMuted, marginBottom:10, display:"flex", alignItems:"center", gap:4}}>
                            <List size={10}/> {mySubTasksDone}/{mySubTasks.length} Unteraufgaben
                          </div>
                        )}

                        <div style={{display:"flex", alignItems:"center", justifyContent:"space-between"}}>
                          <Badge label={tPrio(PRIORITIES[task.priority]?.label)} color={PRIORITIES[task.priority]?.color} bg={PRIORITIES[task.priority]?.bg}/>
                          <div style={{display:"flex"}}>
                            {getTaskAssigneeIds(task).slice(0, 2).map((uid, idx) => <UserAvatar key={uid} user={users.find(u => u.id === uid)} size={22} overlap idx={idx}/>)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  
                  {canEdit && (
                    <div onClick={() => openCreateTask()} style={{padding:"12px", borderRadius:12, border:`2px dashed ${T.border3}`, textAlign:"center", fontSize:12, color:T.textFaint, cursor:"pointer"}} className="hov">
                      + Aufgabe hinzufügen
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}